import React, { useState, useEffect, useContext } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@mui/styles";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import changelog from "../../data/changelog";
import { AuthContext } from "../../context/Auth/AuthContext";
import TriagemMedia from "./TriagemMedia";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(8, 8, 3),
	},

	paper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		marginBottom: 12,
	},

	settingOption: {
		marginLeft: "auto",
	},
	margin: {
		margin: theme.spacing(1),
	},
	changelogSection: {
		marginTop: theme.spacing(4),
	},
	changelogSectionTitle: {
		fontWeight: 700,
		color: "#273A74",
		marginBottom: theme.spacing(2),
	},
	changelogItem: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(2),
		borderLeft: "3px solid #F37021",
		borderRadius: "0 8px 8px 0",
	},
	changelogVersion: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	changelogDate: {
		color: "#8A9096",
		fontSize: 12,
	},
	changelogList: {
		margin: "4px 0 0 16px",
		padding: 0,
		color: "#555A5F",
		fontSize: 13,
	},
	alertSection: {
		marginTop: theme.spacing(3),
		marginBottom: theme.spacing(1),
	},
	alertSectionTitle: {
		fontWeight: 700,
		color: "#273A74",
		marginBottom: theme.spacing(2),
	},
	alertForm: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(2),
	},
	alertSendBtn: {
		alignSelf: "flex-end",
		backgroundColor: "#F37021",
		color: "#fff",
		"&:hover": { backgroundColor: "#d45f10" },
	},
}));

const Settings = () => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);

	const [tab, setTab] = useState(0);
	const [settings, setSettings] = useState([]);
	const [inactivityTimeout, setInactivityTimeout] = useState(
		() => localStorage.getItem("inactivityTimeout") || "30"
	);
	const [slaWarn, setSlaWarn] = useState(() => localStorage.getItem("sla_warning_minutes") || "30");
	const [slaCrit, setSlaCrit] = useState(() => localStorage.getItem("sla_critical_minutes") || "120");
	const [bhEnabled, setBhEnabled] = useState(false);
	const [bhAbsence, setBhAbsence] = useState("");
	const [bhSchedules, setBhSchedules] = useState(() => {
		const days = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];
		return days.map((label, i) => ({
			day: i,
			label,
			active: i >= 1 && i <= 5,
			start: "08:00",
			end: "18:00",
		}));
	});
	const [alertTitle, setAlertTitle] = useState("");
	const [alertBody, setAlertBody] = useState("");
	const [alertSeverity, setAlertSeverity] = useState("info");
	const [alertSending, setAlertSending] = useState(false);
	const [blingStatus, setBlingStatus] = useState({ connected: false, configured: false, expiresInSeconds: 0 });
	const [blingPolling, setBlingPolling] = useState(false);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
				const slaWarnSetting = data.find(s => s.key === "slaWarningMinutes");
				const slaCritSetting = data.find(s => s.key === "slaCriticalMinutes");
				if (slaWarnSetting) { setSlaWarn(slaWarnSetting.value); localStorage.setItem("sla_warning_minutes", slaWarnSetting.value); }
				if (slaCritSetting) { setSlaCrit(slaCritSetting.value); localStorage.setItem("sla_critical_minutes", slaCritSetting.value); }
				const bhSetting = data.find(s => s.key === "businessHours");
				if (bhSetting) {
					try {
						const parsed = JSON.parse(bhSetting.value);
						if (parsed.enabled !== undefined) setBhEnabled(parsed.enabled);
						if (parsed.absenceMessage !== undefined) setBhAbsence(parsed.absenceMessage);
						if (parsed.schedules) {
							const days = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];
							setBhSchedules(days.map((label, i) => ({
								day: i, label,
								active: parsed.schedules[i]?.active ?? (i >= 1 && i <= 5),
								start: parsed.schedules[i]?.start ?? "08:00",
								end: parsed.schedules[i]?.end ?? "18:00",
							})));
						}
					} catch {}
				}
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		socket.on("settings", data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					aux[settingIndex].value = data.setting.value;
					return aux;
				});
			}
		});

		return () => {
			socket.off("settings");
			socket.disconnect();
		};
	}, []);

	const handleChangeSetting = async e => {
		const selectedValue = e.target.value;
		const settingKey = e.target.name;

		try {
			await api.put(`/settings/${settingKey}`, {
				value: selectedValue,
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const getSettingValue = key => {
		const setting = settings.find(s => s.key === key);
		if (!setting) return "";
			return setting.value;
	};

	const handleInactivityChange = (e) => {
		const val = e.target.value;
		setInactivityTimeout(val);
		localStorage.setItem("inactivityTimeout", val);
		toast.success("Timeout de inatividade salvo.");
	};

	const handleSaveSla = async () => {
		try {
			await Promise.all([
				api.put("/settings/slaWarningMinutes", { value: slaWarn }),
				api.put("/settings/slaCriticalMinutes", { value: slaCrit }),
			]);
			localStorage.setItem("sla_warning_minutes", slaWarn);
			localStorage.setItem("sla_critical_minutes", slaCrit);
			toast.success("SLA salvo.");
		} catch (err) { toastError(err); }
	};

	const handleSaveBh = async () => {
		const schedules = {};
		bhSchedules.forEach(s => { schedules[s.day] = { active: s.active, start: s.start, end: s.end }; });
		const value = JSON.stringify({ enabled: bhEnabled, absenceMessage: bhAbsence, schedules });
		try {
			await api.put("/settings/businessHours", { value });
			toast.success("Horário de atendimento salvo.");
		} catch (err) { toastError(err); }
	};

	const updateSchedule = (day, field, val) => {
		setBhSchedules(prev => prev.map(s => s.day === day ? { ...s, [field]: val } : s));
	};

	useEffect(() => {
		if (user.profile !== "admin") return;
		api.get("/bling/status").then(({ data }) => setBlingStatus(data)).catch(() => {});
	}, [user.profile]);

	const handleBlingAuth = async () => {
		try {
			const { data } = await api.get("/bling/auth-url");
			const popup = window.open(data.url, "_blank", "width=700,height=600");
			setBlingPolling(true);
			let attempts = 0;
			const poll = setInterval(async () => {
				attempts++;
				try {
					const { data: st } = await api.get("/bling/status");
					setBlingStatus(st);
					if (st.connected || attempts >= 30) {
						clearInterval(poll);
						setBlingPolling(false);
						if (st.connected) toast.success("Bling conectado com sucesso!");
					}
				} catch {
					clearInterval(poll);
					setBlingPolling(false);
				}
				if (popup?.closed && !blingStatus.connected) {
					clearInterval(poll);
					setBlingPolling(false);
				}
			}, 3000);
		} catch (err) { toastError(err); }
	};

	const handleBlingDisconnect = async () => {
		try {
			await api.delete("/bling/auth");
			setBlingStatus({ connected: false, configured: true, expiresInSeconds: 0 });
			toast.success("Bling desconectado.");
		} catch (err) { toastError(err); }
	};

	const handleSendAlert = async () => {
		if (!alertTitle.trim() || !alertBody.trim()) {
			toast.error("Preencha o título e a mensagem.");
			return;
		}
		setAlertSending(true);
		try {
			await api.post("/admin/alerts", { title: alertTitle.trim(), body: alertBody.trim(), severity: alertSeverity });
			toast.success("Aviso enviado para todos os usuários.");
			setAlertTitle("");
			setAlertBody("");
			setAlertSeverity("info");
		} catch (err) {
			toastError(err);
		}
		setAlertSending(false);
	};

	return (
		<div className={classes.root}>
			<Container className={classes.container} maxWidth={tab === 1 ? "md" : "sm"}>
				<Typography variant="body2" gutterBottom>
					{i18n.t("settings.title")}
				</Typography>

				{user.profile === "admin" && (
					<Tabs
						value={tab}
						onChange={(_e, v) => setTab(v)}
						textColor="inherit"
						TabIndicatorProps={{ style: { backgroundColor: "#F37021" } }}
						style={{ marginBottom: 16 }}
					>
						<Tab label="Geral" />
						<Tab label="Mídias da Triagem" />
					</Tabs>
				)}

				{tab === 1 && user.profile === "admin" && <TriagemMedia />}

				{tab === 0 && (<>
				<Paper className={classes.paper}>
					<Typography variant="body1">
						{i18n.t("settings.settings.userCreation.name")}
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="userCreation-setting"
						name="userCreation"
						value={
							settings && settings.length > 0 && getSettingValue("userCreation")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							{i18n.t("settings.settings.userCreation.options.enabled")}
						</option>
						<option value="disabled">
							{i18n.t("settings.settings.userCreation.options.disabled")}
						</option>
					</Select>

				</Paper>

				<Paper className={classes.paper}>
					<TextField
						id="api-token-setting"
						readonly
						label="Token Api"
						margin="dense"
						variant="outlined"
						fullWidth
						value={settings && settings.length > 0 && getSettingValue("userApiToken")}
					/>
				</Paper>

				<Paper className={classes.paper}>
					<Typography variant="body1">
						Timeout de inatividade (minutos)
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						name="inactivityTimeout"
						value={inactivityTimeout}
						className={classes.settingOption}
						onChange={handleInactivityChange}
					>
						<option value="0">Desativado</option>
						<option value="5">5 min</option>
						<option value="10">10 min</option>
						<option value="15">15 min</option>
						<option value="30">30 min</option>
						<option value="60">60 min</option>
					</Select>
				</Paper>

				{user.profile === "admin" && (
					<div className={classes.alertSection}>
						<Divider style={{ marginBottom: 16 }} />
						<Typography variant="h6" className={classes.alertSectionTitle}>
							Enviar Aviso para Todos
						</Typography>
						<Paper className={classes.paper} style={{ flexDirection: "column", alignItems: "stretch" }}>
							<div className={classes.alertForm}>
								<TextField
									label="Título"
									variant="outlined"
									size="small"
									fullWidth
									value={alertTitle}
									onChange={e => setAlertTitle(e.target.value)}
									inputProps={{ maxLength: 80 }}
								/>
								<TextField
									label="Mensagem"
									variant="outlined"
									size="small"
									fullWidth
									multiline
									rows={3}
									value={alertBody}
									onChange={e => setAlertBody(e.target.value)}
									inputProps={{ maxLength: 500 }}
								/>
								<FormControl variant="outlined" size="small" style={{ width: 160 }}>
									<InputLabel>Tipo</InputLabel>
									<Select
										value={alertSeverity}
										onChange={e => setAlertSeverity(e.target.value)}
										label="Tipo"
									>
										<MenuItem value="info">ℹ️ Informação</MenuItem>
										<MenuItem value="warning">⚠️ Atenção</MenuItem>
										<MenuItem value="error">🚨 Urgente</MenuItem>
									</Select>
								</FormControl>
								<Button
									variant="contained"
									className={classes.alertSendBtn}
									onClick={handleSendAlert}
									disabled={alertSending}
								>
									{alertSending ? "Enviando..." : "Enviar para todos"}
								</Button>
							</div>
						</Paper>
					</div>
				)}

				{user.profile === "admin" && (
					<div style={{ marginTop: 24 }}>
						<Divider style={{ marginBottom: 16 }} />
						<Typography variant="h6" style={{ fontWeight: 700, color: "#273A74", marginBottom: 12 }}>
							SLA de Atendimento
						</Typography>
						<Paper className={classes.paper} style={{ gap: 16 }}>
							<TextField
								label="Alerta (minutos)"
								type="number"
								size="small"
								variant="outlined"
								value={slaWarn}
								onChange={e => setSlaWarn(e.target.value)}
								inputProps={{ min: 1, max: 1440 }}
								style={{ width: 160 }}
							/>
							<TextField
								label="Crítico (minutos)"
								type="number"
								size="small"
								variant="outlined"
								value={slaCrit}
								onChange={e => setSlaCrit(e.target.value)}
								inputProps={{ min: 1, max: 1440 }}
								style={{ width: 160 }}
							/>
							<Button variant="contained" size="small" style={{ backgroundColor: "#F37021", color: "#fff", marginLeft: "auto" }} onClick={handleSaveSla}>
								Salvar SLA
							</Button>
						</Paper>

						<Divider style={{ margin: "16px 0" }} />
						<Typography variant="h6" style={{ fontWeight: 700, color: "#273A74", marginBottom: 12 }}>
							Horário de Atendimento
						</Typography>
						<Paper className={classes.paper} style={{ flexDirection: "column", alignItems: "stretch", gap: 12 }}>
							<FormControlLabel
								label="Ativar mensagem de ausência fora do horário"
								control={<Switch checked={bhEnabled} onChange={e => setBhEnabled(e.target.checked)} color="primary" />}
							/>
							<TextField
								label="Mensagem de ausência"
								multiline
								rows={2}
								size="small"
								variant="outlined"
								fullWidth
								value={bhAbsence}
								onChange={e => setBhAbsence(e.target.value)}
								disabled={!bhEnabled}
								placeholder="Olá! Estamos fora do horário de atendimento..."
							/>
							<div>
								{bhSchedules.map(s => (
									<Grid container key={s.day} alignItems="center" spacing={1} style={{ marginBottom: 4 }}>
										<Grid item style={{ width: 40 }}>
											<Switch
												size="small"
												checked={s.active}
												onChange={e => updateSchedule(s.day, "active", e.target.checked)}
												disabled={!bhEnabled}
												color="primary"
											/>
										</Grid>
										<Grid item style={{ width: 34 }}>
											<Typography variant="caption" style={{ fontWeight: 600 }}>{s.label}</Typography>
										</Grid>
										<Grid item>
											<TextField
												type="time"
												size="small"
												variant="outlined"
												value={s.start}
												onChange={e => updateSchedule(s.day, "start", e.target.value)}
												disabled={!bhEnabled || !s.active}
												inputProps={{ style: { padding: "4px 8px", fontSize: 12 } }}
												style={{ width: 100 }}
											/>
										</Grid>
										<Grid item><Typography variant="caption">às</Typography></Grid>
										<Grid item>
											<TextField
												type="time"
												size="small"
												variant="outlined"
												value={s.end}
												onChange={e => updateSchedule(s.day, "end", e.target.value)}
												disabled={!bhEnabled || !s.active}
												inputProps={{ style: { padding: "4px 8px", fontSize: 12 } }}
												style={{ width: 100 }}
											/>
										</Grid>
									</Grid>
								))}
							</div>
							<Button variant="contained" size="small" style={{ backgroundColor: "#F37021", color: "#fff", alignSelf: "flex-end" }} onClick={handleSaveBh}>
								Salvar Horário
							</Button>
						</Paper>

						<Divider style={{ margin: "16px 0" }} />
						<Typography variant="h6" style={{ fontWeight: 700, color: "#273A74", marginBottom: 12 }}>
							Integração Bling ERP
						</Typography>
						<Paper className={classes.paper} style={{ flexDirection: "column", alignItems: "stretch", gap: 12 }}>
							<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
								<div style={{
									width: 10, height: 10, borderRadius: "50%",
									backgroundColor: blingStatus.connected ? "#4caf50" : "#bdbdbd",
									flexShrink: 0,
								}} />
								<Typography variant="body2" style={{ color: blingStatus.connected ? "#4caf50" : "#8A9096" }}>
									{blingStatus.connected
										? `Conectado${blingStatus.expiresInSeconds > 0 ? ` (expira em ${Math.round(blingStatus.expiresInSeconds / 60)} min)` : ""}`
										: "Não autorizado"}
								</Typography>
							</div>
							<Typography variant="caption" style={{ color: "#8A9096" }}>
								Client ID: {getSettingValue("blingClientId") ? "••••••••" + getSettingValue("blingClientId").slice(-6) : "não configurado"}
							</Typography>
							{blingStatus.connected ? (
								<Button
									variant="outlined"
									size="small"
									style={{ color: "#e53935", borderColor: "#e53935", alignSelf: "flex-start" }}
									onClick={handleBlingDisconnect}
								>
									Desconectar
								</Button>
							) : (
								<Button
									variant="contained"
									size="small"
									disabled={blingPolling || !blingStatus.configured}
									style={{ backgroundColor: "#F37021", color: "#fff", alignSelf: "flex-start" }}
									onClick={handleBlingAuth}
								>
									{blingPolling ? "Aguardando autorização..." : "Autorizar Bling →"}
								</Button>
							)}
							{!blingStatus.configured && (
								<Typography variant="caption" style={{ color: "#e53935" }}>
									Credenciais não configuradas. Contate o suporte técnico.
								</Typography>
							)}
						</Paper>
					</div>
				)}

				<div className={classes.changelogSection}>
					<Divider style={{ marginBottom: 16 }} />
					<Typography variant="h6" className={classes.changelogSectionTitle}>
						Versão e Atualizações
					</Typography>
					{changelog.map((entry) => (
						<Paper key={entry.version} className={classes.changelogItem} variant="outlined">
							<div className={classes.changelogVersion}>
								<Chip
									label={`v${entry.version}`}
									size="small"
									style={{ backgroundColor: "#F37021", color: "#fff", fontWeight: 700 }}
								/>
								<Typography className={classes.changelogDate}>{entry.date}</Typography>
							</div>
							<ul className={classes.changelogList}>
								{entry.changes.map((c, i) => (
									<li key={i}>{c}</li>
								))}
							</ul>
						</Paper>
					))}
				</div>
				</>)}

			</Container>
		</div>
	);
};

export default Settings;
